/* Una scheda dell'archivio /report (`.p-card` del modello del 19/10/2026).

   Foto in bicromia in alto, etichette con «Accesso riservato», titolo
   serif **intero** (nessun `line-clamp`: a monte il più lungo passa i 190
   caratteri), sommario, date e in fondo il piede col codice, appoggiato al
   fondo della scheda (`mt-auto`), così nelle righe della griglia i piedi
   stanno alla stessa quota. Tutta la scheda è un collegamento.

   La prima scheda di ogni pagina è `grande`: due colonne, foto a sinistra
   (52%) alta quanto il testo, che è il passo del modello (una grande più
   una piccola, poi tre piccole: cinque dossier per pagina). Foto e testo
   si affiancano da 1100 e non da 900 come nel modello: a 1024 la colonna
   del testo è stretta, la scheda si allunga e la foto, che ne prende
   l'altezza, usciva verticale (0,60, misurato). Fra 900 e 1100 la grande
   tiene le due colonne con la foto sopra, in 2:1. Lo stesso per l'apertura.

   La foto delle piccole è 2:1 come nel modello (`aspect-ratio: 16/8`), e
   sulle foto a monte, che sono 3:2, taglia un quarto dell'altezza. Provato
   il 3:2 intero (19/10/2026, a 1440): la riga della scheda grande saliva
   da ~460 a ~650 pixel, perché la sua altezza la decide la piccola
   accanto, e sotto il testo della grande restavano ~280 pixel vuoti. Sul
   telefono (sotto i 640), dove una scheda non ha vicine e la foto è
   larga 350, torna 3:2 intera. La
   grande prende l'altezza dalla riga e ritaglia, come nel modello. */

import Link from "next/link";

import { DateDossier, Etichette, FotoDossier, PiedeDossier } from "@/components/report/pezzi";
import { FUOCO_REP, PANNELLO } from "@/components/report/stile";
import type { SchedaReport } from "@/lib/report-dati";

const SIZES =
  "(max-width: 900px) calc(100vw - 38px), (max-width: 1440px) calc((100vw - 120px) / 3), 440px";
const SIZES_GRANDE =
  "(max-width: 900px) calc(100vw - 38px), (max-width: 1100px) 66vw, (max-width: 1440px) 33vw, 480px";

export function SchedaDossier({ s, grande = false }: { s: SchedaReport; grande?: boolean }) {
  const conFoto = Boolean(s.foto);
  return (
    <Link
      href={s.href}
      className={`group/dossier flex min-w-0 flex-col text-ink no-underline ${PANNELLO} ${
        grande ? "min-[901px]:col-span-2" : ""
      } ${grande && conFoto ? "min-[1101px]:flex-row" : ""} ${FUOCO_REP}`}
    >
      {s.foto ? (
        <FotoDossier
          foto={s.foto}
          sizes={grande ? SIZES_GRANDE : SIZES}
          className={
            grande
              ? "aspect-[3/2] min-[640px]:aspect-[2/1] min-[1101px]:aspect-auto min-[1101px]:flex-[0_0_52%]"
              : "aspect-[3/2] min-[640px]:aspect-[2/1]"
          }
        />
      ) : null}
      <span
        className={`flex flex-1 flex-col gap-2.5 px-5 py-[18px] ${
          grande ? "min-[901px]:px-7 min-[901px]:py-6" : "min-[901px]:px-[22px]"
        }`}
      >
        <Etichette s={s} />
        <span
          className={`font-serif leading-[1.3] transition-colors [overflow-wrap:anywhere] group-hover/dossier:text-mare ${
            grande ? "text-[22px] min-[901px]:text-[26px] min-[901px]:leading-[1.2]" : "text-[20px]"
          }`}
        >
          {s.titolo}
        </span>
        {s.sommario ? (
          /* Sopra i 900 le piccole tengono il sommario a tre righe: a monte
             è il `lead`, già tagliato a ~260 caratteri coi puntini, cioè
             cinque righe in una colonna da 400. Senza tetto la piccola
             accanto alla grande decide una riga alta, e sotto il testo della
             grande resta un vuoto (il modello ha sommari da due o tre righe). */
          <span
            className={`font-sans text-sommario leading-[1.5] text-sommesso ${grande ? "" : "min-[901px]:line-clamp-3"}`}
          >
            {s.sommario}
          </span>
        ) : null}
        <DateDossier s={s} />
        <PiedeDossier s={s} />
      </span>
    </Link>
  );
}
